import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useRef } from "react";

import { ApiError } from "../../lib/api";
import { companyQrApi } from "../../lib/api/qr";
import { t } from "../../lib/i18n";
import { Alert } from "../ui/Alert";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { LoadingState } from "../ui/State";

interface Props {
  open: boolean;
  onClose: () => void;
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function CompanyQRModal({ open, onClose }: Props) {
  const printableRef = useRef<HTMLDivElement>(null);

  const qr = useQuery({
    queryKey: ["company", "qr"],
    queryFn: () => companyQrApi.get(),
    enabled: open,
    staleTime: 60_000,
  });

  function handlePrint() {
    if (!qr.data) return;
    const html = printableRef.current?.outerHTML ?? "";
    const win = window.open("", "_blank", "noopener,noreferrer,width=560,height=720");
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>${t("qr.printTitle")}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; padding: 40px; text-align: center; }
  h1 { font-size: 22px; margin: 0 0 8px; }
  p  { color: #555; margin: 0 0 24px; font-size: 14px; }
  .code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; word-break: break-all; margin-top: 16px; color: #444; }
  svg { display: block; margin: 0 auto; }
  @media print { body { padding: 12mm; } }
</style></head><body>${html}<script>window.onload=()=>{window.print();}</script></body></html>`);
    win.document.close();
  }

  return (
    <Modal open={open} onClose={onClose} title={t("qr.title")}>
      {qr.isLoading && <LoadingState />}
      {qr.error && (
        <Alert tone="danger">{translateError(qr.error)}</Alert>
      )}
      {qr.data && (
        <>
          <div
            ref={printableRef}
            style={{
              background: "#fff",
              padding: 24,
              borderRadius: 12,
              border: "1px solid var(--color-border, #e5e5e5)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <h1 style={{ margin: 0, fontSize: 18 }}>{t("qr.printTitle")}</h1>
            <p style={{ margin: 0, color: "var(--color-text-muted, #555)" }}>
              {t("qr.printSubtitle")}
            </p>
            <QRCodeSVG
              value={JSON.stringify(qr.data.qr_payload)}
              size={256}
              includeMargin
              level="M"
            />
            <div className="code" style={{ marginTop: 12, fontSize: 12 }}>
              {qr.data.raw_code}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handlePrint}>{t("qr.print")}</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
